import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Send, AlertCircle, HelpCircle, Upload, FileIcon } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { supportTicketService } from '../services/supportTicketService';
import { customToast } from '../utils/customToast';

interface SupportTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const SupportTicketModal: React.FC<SupportTicketModalProps> = ({ isOpen, onClose, onSuccess: _onSuccess }) => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    category: 'general',
    priority: 'medium',
    attachment_url: ''
  });

  const categories = [
    'general',
    'technical-issue',
    'billing',
    'feature-request',
    'bug-report',
    'account',
    'other'
  ];

  const priorities = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'urgent', label: 'Urgent' }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileAttach = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    // Limit to 5 files and 10MB each
    const validFiles = files.filter(file => {
      if (file.size > 10 * 1024 * 1024) {
        customToast.error(`${file.name} is too large (max 10MB)`);
        return false;
      }
      return true;
    });
    
    if (attachedFiles.length + validFiles.length > 5) {
      customToast.error('Maximum 5 files allowed');
      return;
    }
    
    setAttachedFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.subject.trim() || !formData.description.trim()) {
      customToast.error('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      
      // Create FormData to handle file upload
      const uploadFormData = new FormData();
      uploadFormData.append('subject', formData.subject);
      uploadFormData.append('description', formData.description);
      uploadFormData.append('category', formData.category);
      uploadFormData.append('priority', formData.priority);
      
      // Append all files
      attachedFiles.forEach((file, index) => {
        uploadFormData.append(`attachments[${index}]`, file);
      });
      
      await supportTicketService.createTicketWithAttachment(uploadFormData);
      
      customToast.success('Support ticket created successfully! We\'ll be in touch soon.');
      
      // Reset form and close modal
      setFormData({
        subject: '',
        description: '',
        category: 'general',
        priority: 'medium',
        attachment_url: ''
      });
      setAttachedFiles([]);
      onClose();
      
      // Call the success callback if provided
      if (_onSuccess) {
        _onSuccess();
      }
    } catch (error: any) {
      console.error('Failed to create ticket:', error);
      customToast.error(error.response?.data?.message || 'Failed to create support ticket');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-[9999] animate-fadeIn"
      onClick={onClose}
    >
      <div 
        className={`w-full max-w-2xl max-h-[90vh] flex flex-col rounded-xl shadow-2xl animate-scaleIn ${
          theme === 'dark' ? 'bg-slate-800' : 'bg-white'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`px-4 sm:px-5 md:px-6 py-3 sm:py-4 border-b flex-shrink-0 rounded-t-xl ${
          theme === 'dark' ? 'border-slate-700 bg-slate-800/95' : 'border-slate-200 bg-gray-50/95'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 min-w-0 flex-1">
              <div className={`p-1.5 rounded-lg flex-shrink-0 ${
                theme === 'dark' ? 'bg-amber-900/50' : 'bg-amber-100'
              }`}>
                <HelpCircle className={`w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 ${
                  theme === 'dark' ? 'text-amber-400' : 'text-amber-600'
                }`} />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className={`text-sm sm:text-base md:text-lg font-semibold truncate ${
                  theme === 'dark' ? 'text-white' : 'text-slate-900'
                }`}>
                  Get Support & Help
                </h2>
                <p className={`text-xs truncate ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  Submit a support ticket to our team
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className={`p-2 rounded-lg transition-colors touch-manipulation flex-shrink-0 ${
                theme === 'dark' 
                  ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' 
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className={`flex-1 overflow-y-auto ${
          theme === 'dark' ? 'bg-slate-800' : 'bg-white'
        }`}>
          <div className="p-4 sm:p-5 md:p-6">
            <form onSubmit={handleSubmit} className="space-y-5">
                {/* Info Box */}
                <div className={`p-3 sm:p-4 rounded-lg border-l-4 ${
                  theme === 'dark'
                    ? 'bg-blue-900/20 border-blue-600'
                    : 'bg-blue-50 border-blue-600'
                }`}>
                  <div className="flex items-start gap-3">
                    <AlertCircle className={`w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5 ${
                      theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                    }`} />
                    <div className="min-w-0 flex-1">
                      <p className={`text-xs sm:text-sm font-medium ${
                        theme === 'dark' ? 'text-blue-400' : 'text-blue-900'
                      }`}>
                        Shop Details
                      </p>
                      <p className={`text-xs sm:text-sm mt-1 ${
                        theme === 'dark' ? 'text-blue-300' : 'text-blue-800'
                      }`}>
                        Your shop information will be automatically attached to this ticket.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Subject */}
                <div>
                  <label className={`block text-xs sm:text-sm font-medium mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Subject *
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    placeholder="Brief summary of your issue"
                    className={`w-full px-3 py-2 text-sm rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                      theme === 'dark'
                        ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-400 focus:border-blue-500'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500'
                    }`}
                  />
                </div>

                {/* Category and Priority */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className={`block text-xs sm:text-sm font-medium mb-2 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Category *
                    </label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 text-sm rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                        theme === 'dark'
                          ? 'bg-slate-700 border-slate-600 text-white focus:border-blue-500'
                          : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                      }`}
                    >
                      {categories.map(cat => (
                        <option key={cat} value={cat}>
                          {cat.replace('-', ' ').charAt(0).toUpperCase() + cat.replace('-', ' ').slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={`block text-xs sm:text-sm font-medium mb-2 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Priority *
                    </label>
                    <select
                      name="priority"
                      value={formData.priority}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 text-sm rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                        theme === 'dark'
                          ? 'bg-slate-700 border-slate-600 text-white focus:border-blue-500'
                          : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                      }`}
                    >
                      {priorities.map(pri => (
                        <option key={pri.value} value={pri.value}>
                          {pri.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className={`block text-xs sm:text-sm font-medium mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Description *
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Please provide detailed information about your issue..."
                    rows={4}
                    className={`w-full px-3 py-2 text-sm rounded-lg border transition-colors resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                      theme === 'dark'
                        ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-400 focus:border-blue-500'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500'
                    }`}
                  />
                </div>

                {/* File Attachments */}
                <div>
                  <label className={`block text-xs sm:text-sm font-medium mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Attach Screenshots or Files (Max 5 files, 10MB each)
                  </label>
                  <div className={`p-4 border-2 border-dashed rounded-lg text-center cursor-pointer transition-colors ${
                    theme === 'dark'
                      ? 'border-slate-600 hover:border-blue-500 hover:bg-slate-700/50'
                      : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50'
                  }`}>
                    <input
                      type="file"
                      multiple
                      onChange={handleFileAttach}
                      className="hidden"
                      id="file-input"
                      accept="image/*,.pdf,.doc,.docx"
                    />
                    <label htmlFor="file-input" className="cursor-pointer block">
                      <Upload className={`w-6 h-6 mx-auto mb-2 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`} />
                      <p className={`text-xs sm:text-sm font-medium ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Click to upload or drag and drop
                      </p>
                      <p className={`text-xs mt-1 ${
                        theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                      }`}>
                        Images, PDF, Documents
                      </p>
                    </label>
                  </div>

                  {/* Attached Files Preview */}
                  {attachedFiles.length > 0 && (
                    <div className="mt-3 space-y-2">
                      <p className={`text-xs font-medium ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        Attached Files: {attachedFiles.length}/5
                      </p>
                      {attachedFiles.map((file, index) => (
                        <div
                          key={index}
                          className={`flex items-center justify-between p-2 rounded ${
                            theme === 'dark' ? 'bg-slate-700' : 'bg-gray-100'
                          }`}
                        >
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <FileIcon className="w-4 h-4 flex-shrink-0" />
                            <span className={`text-xs truncate ${
                              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                              {file.name}
                            </span>
                            <span className={`text-xs flex-shrink-0 ${
                              theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                            }`}>
                              ({(file.size / 1024 / 1024).toFixed(2)}MB)
                            </span>
                          </div>
                          <button
                            onClick={() => removeFile(index)}
                            className={`p-1 rounded transition-colors flex-shrink-0 ${
                              theme === 'dark'
                                ? 'hover:bg-slate-600 text-gray-400'
                                : 'hover:bg-gray-200 text-gray-600'
                            }`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </form>
          </div>
        </div>

        {/* Footer Actions */}
        <div className={`px-4 sm:px-5 md:px-6 py-3 sm:py-4 border-t flex-shrink-0 rounded-b-xl flex justify-end gap-2 sm:gap-3 ${
          theme === 'dark' ? 'border-slate-700 bg-slate-800/95' : 'border-slate-200 bg-gray-50/95'
        }`}>
          <button
            onClick={onClose}
            className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-lg transition-colors touch-manipulation ${
              theme === 'dark'
                ? 'text-gray-300 hover:text-white hover:bg-slate-700'
                : 'text-gray-700 hover:text-gray-900 hover:bg-gray-200'
            }`}
          >
            Cancel
          </button>
          <button
            onClick={() => {
              const form = document.querySelector('form');
              if (form) form.dispatchEvent(new Event('submit', { bubbles: true }));
            }}
            disabled={loading}
            className={`flex items-center gap-2 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium rounded-lg text-white transition-colors touch-manipulation ${
              loading
                ? 'bg-blue-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
            }`}
          >
            <Send className="w-4 h-4" />
            <span className="hidden sm:inline">{loading ? 'Submitting...' : 'Submit Ticket'}</span>
            <span className="sm:hidden">{loading ? '...' : 'Submit'}</span>
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
